// Forter fraud-prevention loader.
// Loads the per-site Forter script, then exposes the latest Forter token.
// Forter dispatches 'ftr:tokenReady' (verified from the live script.js); some
// docs reference 'ftr_tokenready'. We listen for both to be safe.
import type { ConnectionInformation } from '@linktivity/ars-neo';

const TOKEN_EVENTS = ['ftr:tokenReady', 'ftr_tokenready'] as const;
const SCRIPT_ID = 'forter-script';

let initialized = false;
let forterToken: string | undefined;

function readTokenCookie(): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }
  const match = document.cookie.match(/(?:^|;\s*)forterToken=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : undefined;
}

function handleTokenReady(event: Event): void {
  const detail = (event as CustomEvent<string>).detail;
  if (typeof detail === 'string' && detail) {
    forterToken = detail;
  }
}

/**
 * Load Forter for the given site id. Safe to call once at app boot.
 * No-op outside the browser (SSR / prerender) or without a site id.
 */
export function initForter(siteId?: string): void {
  if (initialized) {
    return;
  }
  // No-op (without marking initialized) when unconfigured or off-browser, so a
  // later call with a valid siteId — e.g. a different boot path — still runs.
  if (!siteId || typeof document === 'undefined') {
    return;
  }
  initialized = true;

  // Register listeners BEFORE injecting the snippet so no event is missed.
  TOKEN_EVENTS.forEach(eventName => {
    document.addEventListener(eventName, handleTokenReady);
  });

  // Guard against a duplicate tag (e.g. HMR re-evaluating this module).
  if (document.getElementById(SCRIPT_ID)) {
    return;
  }
  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.async = true;
  script.src = `https://${siteId}.cdn4.forter.com/sn/${siteId}/script.js`;
  document.head.appendChild(script);
}

/** Latest Forter token, or undefined if not yet available. */
export function getForterToken(): string | undefined {
  return forterToken ?? readTokenCookie();
}

/**
 * ConnectionInformation carrying the Forter token, for requests screened by
 * fraud prevention (ExchangeToken, CreatePayment). Undefined when no token is
 * available yet, so the field is omitted from the request body entirely.
 */
export function getForterConnectionInformation():
  | ConnectionInformation
  | undefined {
  const token = getForterToken();
  // Token is only available in browser contexts; still guard navigator for non-standard runtimes/tests.
  if (!token) return undefined;
  return {
    forterTokenCookie: token,
    ...(typeof navigator !== 'undefined'
      ? { userAgent: navigator.userAgent }
      : {})
  };
}
